import React, { useMemo, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import DashboardPage from './DashboardPage';
import { playerAPI } from '../services/api';
import {
  normalizePlayerTeamSection,
  buildPlayerTeamWorkspacePath,
  rememberPlayerTeamWorkspace,
  readRememberedPlayerTeamWorkspace,
  setLastActiveTeamWorkspaceId,
} from '../utils/playerTeamWorkspace';
import { collectPlayerTeamWorkspaceDeepLinkParams } from '../utils/playerNotificationNavigation';

const Wrap = styled.div`
  width: 100%;
  max-width: none;
  margin: 0;
`;

export default function PlayerTeamWorkspacePage() {
  const { t } = useTranslation();
  const { teamId: teamIdParam, section: sectionParam } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const embeddedSection = normalizePlayerTeamSection(sectionParam);

  const tenantQ = searchParams.get('tenant_id') || '';
  const seasonQ = searchParams.get('season_id') || '';
  const campaignQ = searchParams.get('campaign_id') || '';

  const { data, isLoading } = useQuery(['playerTeams'], () => playerAPI.getTeams(), { staleTime: 60 * 1000 });
  const teams = useMemo(
    () => (Array.isArray(data?.data) ? data.data : data?.data?.data) || [],
    [data]
  );

  const teamRow = useMemo(
    () => teams.find((t) => String(t.teamId || t.team_id) === String(teamIdParam)),
    [teams, teamIdParam]
  );

  const remembered = useMemo(() => readRememberedPlayerTeamWorkspace(teamIdParam || ''), [teamIdParam]);

  const resolvedTenant = tenantQ || teamRow?.tenantId || remembered?.tenant_id || '';
  const resolvedSeason = seasonQ || teamRow?.latestSeasonId || remembered?.season_id || '';
  const resolvedCampaign = campaignQ || teamRow?.campaignId || remembered?.campaign_id || '';

  const { data: campaignsRes } = useQuery(
    ['playerCampaigns', resolvedTenant],
    () => playerAPI.getCampaigns(resolvedTenant),
    { enabled: !!resolvedTenant && !!resolvedCampaign, staleTime: 60 * 1000 }
  );
  const selectedCampaign = useMemo(() => {
    const raw = Array.isArray(campaignsRes?.data) ? campaignsRes.data : campaignsRes?.data?.data ?? [];
    return (raw || []).find((c) => String(c.id) === String(resolvedCampaign));
  }, [campaignsRes, resolvedCampaign]);

  /** Fill missing query params from roster row or session (shareable URL after first visit). */
  useEffect(() => {
    if (!teamIdParam || isLoading || !teamRow) return;
    if (tenantQ && seasonQ && campaignQ) return;
    const t = resolvedTenant;
    const s = resolvedSeason;
    const c = resolvedCampaign;
    if (!t || !s || !c) return;
    if (tenantQ === t && seasonQ === s && campaignQ === c) return;
    const deep = collectPlayerTeamWorkspaceDeepLinkParams(searchParams);
    navigate(
      buildPlayerTeamWorkspacePath(teamIdParam, embeddedSection, {
        tenant_id: t,
        season_id: s,
        campaign_id: c,
        ...deep,
      }),
      { replace: true }
    );
  }, [
    teamIdParam,
    isLoading,
    teamRow,
    tenantQ,
    seasonQ,
    campaignQ,
    resolvedTenant,
    resolvedSeason,
    resolvedCampaign,
    embeddedSection,
    navigate,
    searchParams,
  ]);

  useEffect(() => {
    if (teamIdParam && resolvedTenant && resolvedSeason && resolvedCampaign) {
      rememberPlayerTeamWorkspace({
        teamId: teamIdParam,
        tenant_id: resolvedTenant,
        season_id: resolvedSeason,
        campaign_id: resolvedCampaign,
      });
    }
  }, [teamIdParam, resolvedTenant, resolvedSeason, resolvedCampaign]);

  useEffect(() => {
    if (teamIdParam && resolvedTenant && resolvedSeason && resolvedCampaign) {
      setLastActiveTeamWorkspaceId(teamIdParam);
    }
  }, [teamIdParam, resolvedTenant, resolvedSeason, resolvedCampaign]);

  const onEmbeddedSectionNavigate = useCallback(
    (sectionKey) => {
      if (!teamIdParam) return;
      const sec = normalizePlayerTeamSection(sectionKey);
      const deep = collectPlayerTeamWorkspaceDeepLinkParams(searchParams);
      navigate(
        buildPlayerTeamWorkspacePath(teamIdParam, sec, {
          tenant_id: resolvedTenant,
          season_id: resolvedSeason,
          campaign_id: resolvedCampaign,
          ...deep,
        })
      );
    },
    [navigate, teamIdParam, resolvedTenant, resolvedSeason, resolvedCampaign, searchParams]
  );

  if (!teamIdParam) {
    return <Navigate to="/player/teams" replace />;
  }

  if (!isLoading && teams.length > 0 && !teamRow) {
    return <Navigate to="/player/teams" replace />;
  }

  const canRenderDash = !!resolvedTenant && !!resolvedSeason && !!resolvedCampaign && !!teamIdParam;

  if (!isLoading && teamRow && !canRenderDash) {
    return (
      <Wrap style={{ padding: '1.25rem' }}>
        <p style={{ color: '#475569', margin: 0 }}>
          <Trans i18nKey="playerTeamWorkspacePage.noDashboardLink" components={{ strong: <strong /> }} />
        </p>
      </Wrap>
    );
  }

  return (
    <Wrap>
      {canRenderDash ? (
        <DashboardPage
          embedded
          embeddedShowTeamSidebar={false}
          embeddedTeamId={teamIdParam}
          embeddedSeasonId={resolvedSeason}
          embeddedCampaignId={resolvedCampaign}
          embeddedCampaignViewOnly={!!selectedCampaign?.campaign_completed}
          embeddedSection={embeddedSection}
          onEmbeddedSectionNavigate={onEmbeddedSectionNavigate}
        />
      ) : (
        <div style={{ padding: '1.25rem', color: '#64748b' }}>{t('playerTeamWorkspacePage.loadingWorkspace')}</div>
      )}
    </Wrap>
  );
}
